import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import useAuth from '../hooks/useAuth';

const LS_KEY   = 'trainaxis_theme';
const USER_KEY = 'trainaxis_user';
const VALID    = new Set(['light', 'dark']);

// Map old theme names to new ones (for localStorage migration)
const LEGACY = { red: 'light', glass: 'dark', orange: 'light' };

function resolveTheme(saved) {
  if (VALID.has(saved))        return saved;
  if (LEGACY[saved])           return LEGACY[saved];
  return 'light';
}

// ── Single source of truth for applying a theme to the DOM ──────────────────────
// Applies to BOTH <html> and <body>: data-theme attribute + dark/light class, so
// every selector strategy (attribute or class) resolves consistently. Idempotent
// at the attribute level so it never triggers redundant mutations.
export function applyThemeToDom(name) {
  const theme = resolveTheme(name);
  const root  = document.documentElement;
  const body  = document.body;
  const isDark = theme === 'dark';

  if (root.dataset.theme !== theme) root.dataset.theme = theme;
  if (body.dataset.theme !== theme) body.dataset.theme = theme;
  root.classList.toggle('dark',  isDark);
  body.classList.toggle('dark',  isDark);
  root.classList.toggle('light', !isDark);
  body.classList.toggle('light', !isDark);

  return theme;
}

// Initial theme precedence: the cached signed-in user's saved dashboardTheme
// (authoritative, from backend) → local fallback → light. This prevents a stale
// local 'light' from overriding a backend 'dark' on first paint.
function initialTheme() {
  try {
    const cached = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    if (cached?.dashboardTheme) return resolveTheme(cached.dashboardTheme);
  } catch { /* ignore malformed cache */ }
  return resolveTheme(localStorage.getItem(LS_KEY));
}

// The club's saved theme preference (resolved). Exported so SuperAdminLayout can restore it
// on exit — superadmin forces dark while mounted but never writes the shared preference.
export function getStoredTheme() {
  return initialTheme();
}

// Superadmin pages are ALWAYS dark and must ignore the club theme preference, so the provider
// skips applying the stored/club theme while on a /superadmin route (SuperAdminLayout owns the
// dark class there). Read at effect-run time via the live pathname.
function isSuperAdminRoute() {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/superadmin');
}

export const THEMES = {
  light: { primary: '#ef4444', label: 'Light' },
  dark:  { primary: '#ef4444', label: 'Dark'  },
};

export const THEME_LIST = Object.entries(THEMES).map(([name, t]) => ({
  name,
  label: t.label,
  color: t.primary,
}));

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const { user } = useAuth();

  const [currentTheme, setCurrentTheme] = useState(initialTheme);

  const applyTheme = useCallback((name) => {
    const theme = applyThemeToDom(name);
    localStorage.setItem(LS_KEY, theme);
    setCurrentTheme(theme);
  }, []);

  // Apply the initial theme to the DOM on first mount (no flash). Skip on superadmin routes —
  // SuperAdminLayout forces dark there and the club theme must not leak in.
  useEffect(() => {
    if (isSuperAdminRoute()) return;
    applyTheme(currentTheme);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The signed-in user's dashboardTheme (synced from the backend) is the source
  // of truth: whenever it's known, apply it and keep localStorage in sync. This
  // guarantees a saved Dark preference is honored across refresh, new tabs, and
  // logins — never overridden by a stale local theme. Skipped on superadmin routes so a
  // lingering club session can't flip the always-dark superadmin panel to light.
  useEffect(() => {
    if (isSuperAdminRoute()) return;
    if (user?.dashboardTheme) {
      applyTheme(user.dashboardTheme);
    }
  }, [user?.dashboardTheme, applyTheme]);

  return (
    <ThemeContext.Provider value={{ currentTheme, applyTheme, themes: THEME_LIST }}>
      {children}
    </ThemeContext.Provider>
  );
}

export default function useTheme() {
  return useContext(ThemeContext);
}
